import classNames from "classnames";
import React, { ChangeEvent, ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { RootState } from "src/redux/reducers";
import { flexRender } from "@tanstack/react-table";
import { SortableHandle } from "react-sortable-hoc";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import { TasksColumn } from "src/redux/task/taskReducer";
import { Column } from "src/redux/dictionary/types";
import { ProjectPermissionsEnum } from "src/utils/PermissionsEnums";
import { ReactComponent as ClientOrangeIcon } from "src/images/client-orange.svg";
import { ReactComponent as ClientGreyIcon } from "src/images/client-grey.svg";
import { ReactComponent as GridIcon } from "src/images/grid-grey.svg";
import { ReactComponent as HideIcon } from "src/images/hide.svg";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import { setSelectedTaskColumns } from "src/redux";
import { SkeletonText } from "../../../components/Skeleton/Skeleton";
import { TaskTableHeaderOptionsDropdown } from "../../../dropdowns/TaskTableHeaderOptionsDropdown/TaskTableHeaderOptionsDropdown";
import ShareWithClientModal from "../../../modals/ShareWithClientModal/ShareWithClientModal";
import StopSharingWithClientModal from "../../../modals/StopSharingWithClientModal/StopSharingWithClientModal";
import { ColumnHeaderName } from "./ColumnHeaderName";
import SortingTasksArrow from "./tasksTableSorting/SortingTasksArrow/SortingTasksArrow";
import getCommonPinningStyles from "./functions";
import { TableContext } from "../../TableField/types";

export const SKELETON_BLACKLIST = ["select", "drag-handle"];

const isTasksColumn = (column: unknown): column is TasksColumn => {
  if (typeof column === "object" && column !== null) {
    return "metaFieldId" in column;
  }
  return false;
};

const isColumn = (column: unknown): column is Column => {
  if (typeof column === "object" && column !== null) {
    return "uuid" in column;
  }
  return false;
};

const DragHandle = SortableHandle<{ draggable: boolean | undefined }>(
  ({ draggable }: { draggable: boolean | undefined }) => (
    <div className="tasks-table__th-drag-and-drop-wrapper">
      {draggable ? (
        <GridIcon className="tasks-table__th-drag-and-drop" />
      ) : null}
    </div>
  ),
);

const DragHandleWrapper = SortableHandle<{ children: ReactNode }>(
  ({ children }: { children: ReactNode }) => <>{children}</>,
);

const COLUMN_ID_BLACKLIST = [
  "fullWidthTable",
  " action",
  "rightPadding",
  "select",
  "drag-handle",
];

interface HeaderCellBaseProps {
  header: any;
  draggable: boolean | undefined;
  tasksLoading: boolean;
  columnIndex: number;
  isDropdownOpened: boolean;
  setIsDropdownOpened: React.Dispatch<React.SetStateAction<boolean>>;
  activeSortingColumn: string | null;
  tableContext: TableContext | undefined;
}

interface HeaderCellContentForColumnProps extends HeaderCellBaseProps {
  columnFromApi: Column;
}

function HeaderCellContentForColumn({
  header,
  columnFromApi,
  draggable,
  tasksLoading,
  columnIndex,
  isDropdownOpened,
  setIsDropdownOpened,
  activeSortingColumn,
  tableContext,
}: HeaderCellContentForColumnProps) {
  const { column } = header;

  const key = columnFromApi?.name.split(" ").join("_").toLowerCase();
  return (
    <th
      className={classNames("tasks-table__th", {
        "tasks-table__th--transparent-bg":
          tableContext === "workspaceTasksList",
      })}
      style={{
        ...getCommonPinningStyles(header.column, draggable),
      }}
    >
      {tasksLoading ? (
        <>
          <span style={{ visibility: "hidden" }} />
          <SkeletonText width={60} height={15} />
        </>
      ) : (
        <div
          className={classNames("tasks-table__header-wrapper", {
            "tasks-table__header-wrapper--active": isDropdownOpened,
          })}
          data-column-name={columnFromApi?.name}
          data-column-key={key}
        >
          <DragHandle draggable={draggable} />
          {activeSortingColumn === header.id && (
            <SortingTasksArrow tableContext={tableContext} />
          )}
          <ColumnHeaderName column={column} showNameTooltip={columnIndex > 1}>
            {column.columnDef.header}
          </ColumnHeaderName>
          <TaskTableHeaderOptionsDropdown
            uuid={header.id}
            setIsDropdownOpened={setIsDropdownOpened}
            activeSortingColumn={activeSortingColumn}
            tableContext={tableContext}
          />
        </div>
      )}
    </th>
  );
}

interface HeaderCellContentForTaskColumnProps extends HeaderCellBaseProps {
  columnFromApi: TasksColumn;
  draggedColumnId?: string | null;
  isBeingDragged?: boolean;
}

function HeaderCellContentForTaskColumn({
  header,
  columnFromApi,
  columnIndex,
  tasksLoading,
  draggable,
  isDropdownOpened,
  setIsDropdownOpened,
  activeSortingColumn,
  tableContext,
  draggedColumnId,
  isBeingDragged,
}: HeaderCellContentForTaskColumnProps) {
  const [showShareWithClientModal, setShowShareWihClientModal] =
    useState(false);
  const [showStopSharingWithClientModal, setShowStopSharingWithClientModal] =
    useState(false);

  const permissions = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData?.permissions,
  );
  const clientMode = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData?.clientMode,
  );
  const selectedTaskColumns = useSelector(
    (state: RootState) => state.taskReducer.selectedTaskColumns,
  );

  const isSelected = Boolean(
    selectedTaskColumns.find((item) => item.metaFieldId === header.column.id),
  );

  const hideColumn =
    draggedColumnId && isSelected && draggedColumnId !== header.column.id;

  const dispatch = useDispatch();

  const { column } = header;

  const isColumnSharedAndEditable =
    columnFromApi.metaFieldSharedEditable && columnFromApi.metaFieldShared;
  const isColumnSharedAndNotEditable =
    columnFromApi.metaFieldShared && !columnFromApi.metaFieldSharedEditable;

  const showShareResourcesTooltip =
    permissions?.project.includes(
      ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES_PREVIEW,
    ) &&
    clientMode &&
    columnFromApi &&
    columnFromApi?.metaFieldType !== MetaFieldType.DictionaryElement;

  const openShareWithClientModal = () => {
    if (
      permissions?.project.includes(
        ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
      )
    ) {
      setShowShareWihClientModal(true);
    }
  };

  const openStopSharingWithClientModal = () => {
    if (
      permissions?.project.includes(
        ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
      )
    ) {
      setShowStopSharingWithClientModal(true);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    let newSelectedTaskColumns = [];

    if (checked) {
      newSelectedTaskColumns = [...selectedTaskColumns, columnFromApi];
    } else {
      newSelectedTaskColumns = selectedTaskColumns.filter(
        (column) => column.metaFieldId !== header.column.id,
      );
    }
    dispatch(setSelectedTaskColumns(newSelectedTaskColumns));
  };

  const showCounter =
    isBeingDragged &&
    selectedTaskColumns.find((item) => item.metaFieldId === header.column.id);

  return (
    <th
      className={classNames("tasks-table__th", {
        "tasks-table__th--hidden": hideColumn,
        "tasks-table__th--is-dragged": draggedColumnId === header.column.id,
        "tasks-table__th--transparent-bg":
          tableContext === "workspaceTasksList",
      })}
      style={{
        ...getCommonPinningStyles(header.column, draggable),
      }}
    >
      {tasksLoading ? (
        <>
          <span style={{ visibility: "hidden" }} />
          <SkeletonText width={60} height={15} />
        </>
      ) : (
        <div
          className={classNames("tasks-table__header-wrapper", {
            "tasks-table__header-wrapper--active": isDropdownOpened,
          })}
          data-column-name={columnFromApi?.metaFieldName}
          data-column-key={columnFromApi?.metaFieldKey}
        >
          <CustomCheckbox
            className={classNames("tasks-table__th-checkbox", {
              "tasks-table__th-checkbox--checked": isSelected,
            })}
            id={header.id}
            name={columnFromApi.metaFieldName}
            onChange={onChange}
            checked={isSelected}
          />
          {/* <DragHandle draggable={draggable} /> */}
          {activeSortingColumn === header.id && (
            <SortingTasksArrow tableContext={tableContext} />
          )}
          <DragHandleWrapper>
            <ColumnHeaderName
              column={column}
              showShareResourcesTooltip={showShareResourcesTooltip}
              showNameTooltip={columnIndex > 1}
              draggable={draggable}
            >
              {column.columnDef.header}
            </ColumnHeaderName>
          </DragHandleWrapper>

          {showCounter && (
            <div className="tasks-table__th-counter">
              +{selectedTaskColumns.length - 1}
            </div>
          )}

          {showShareResourcesTooltip && (
            <TooltipPortal
              className="tasks-table__header-wrapper-share-icon"
              center
              content={
                columnFromApi.metaFieldShared ||
                columnFromApi.metaFieldIsSystem ? (
                  <IDHFormattedMessage
                    id="ws_tooltip_click_to_stop_sharing"
                    defaultMessage="Click to stop sharing with Client"
                  />
                ) : (
                  <IDHFormattedMessage
                    id="ws_tooltip_click_to_share"
                    defaultMessage="Click to share with Client"
                  />
                )
              }
              contentHidden={
                !permissions?.project.includes(
                  ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
                )
              }
            >
              {isColumnSharedAndNotEditable || isColumnSharedAndEditable ? (
                <ClientOrangeIcon
                  className={classNames("tasks-table__column-shared", {
                    "tasks-table__column-shared--disabled":
                      !permissions?.project.includes(
                        ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
                      ),
                    "tasks-table__column-shared--gray":
                      isColumnSharedAndNotEditable,
                  })}
                  onClick={openStopSharingWithClientModal}
                />
              ) : (
                <ClientGreyIcon
                  className={classNames("tasks-table__column-not-shared", {
                    "tasks-table__column-not-shared--disabled":
                      !permissions?.project.includes(
                        ProjectPermissionsEnum.SHARE_PROJECT_RESOURCES,
                      ),
                  })}
                  onClick={openShareWithClientModal}
                />
              )}
            </TooltipPortal>
          )}

          {columnFromApi?.metaFieldIsAccessLimited && (
            <TooltipPortal
              className="tasks-table__header-wrapper-access-limited-icon"
              center
              content={
                <IDHFormattedMessage
                  id="ws_tooltip_field_access_limited"
                  defaultMessage="Editing this field is limited to specific members"
                />
              }
            >
              <HideIcon className="tasks-table__column-access-limited" />
            </TooltipPortal>
          )}

          <TaskTableHeaderOptionsDropdown
            uuid={header.id}
            columnFromApi={columnFromApi}
            setIsDropdownOpened={setIsDropdownOpened}
            openShareWithClientModal={openShareWithClientModal}
            openStopSharingWithClientModal={openStopSharingWithClientModal}
            tableContext={tableContext}
            activeSortingColumn={activeSortingColumn}
          />
        </div>
      )}

      {showShareWithClientModal && (
        <ShareWithClientModal
          onClose={() => setShowShareWihClientModal(false)}
          columnFromApi={columnFromApi}
        />
      )}

      {showStopSharingWithClientModal && (
        <StopSharingWithClientModal
          onClose={() => setShowStopSharingWithClientModal(false)}
          metaFieldId={header.id}
        />
      )}
    </th>
  );
}

interface HeaderCellProps {
  header: any;
  columnIndex: number;
  tasksLoading: boolean;
  draggable?: boolean;
  totalRowCount?: number;
  currentRowCount?: number;
  tableContext: TableContext | undefined;
  draggedColumnId?: string | null;
  isBeingDragged?: boolean;
}

function HeaderCell({
  header,
  columnIndex,
  tasksLoading,
  draggable,
  totalRowCount,
  currentRowCount,
  tableContext,
  draggedColumnId,
  isBeingDragged,
}: HeaderCellProps) {
  const { column } = header;
  const { columnFromApi }: { columnFromApi: TasksColumn | Column | undefined } =
    column?.columnDef;
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const { activeSortingColumn: taskActiveSortingColumn } = useSelector(
    (state: RootState) => state.taskFiltersReducer,
  );

  const { activeSortingColumn: workspaceActiveSortingColumn } = useSelector(
    (state: RootState) => state.workspaceTaskFiltersReducer,
  );

  const activeSortingColumn =
    tableContext === "workspaceTasksList"
      ? workspaceActiveSortingColumn
      : taskActiveSortingColumn;

  if (columnFromApi === undefined && column.id) {
    const renderContent = () => {
      if (column.id === "select") {
        return flexRender(header.column.columnDef.header, header.getContext());
      }

      if (columnIndex === 2) {
        return (
          <ColumnHeaderName column={column}>
            DISPLAYING: {currentRowCount} OF {totalRowCount}
          </ColumnHeaderName>
        );
      }

      return (
        <ColumnHeaderName column={column} showNameTooltip={columnIndex > 1}>
          {column.columnDef.header}
        </ColumnHeaderName>
      );
    };

    return (
      <th
        className={classNames("tasks-table__th", {
          "tasks-table__th--action": column.id === " action",
          "tasks-table__th--transparent-bg":
            tableContext === "workspaceTasksList",
        })}
        id={`header-${column.id}`}
        style={{
          ...getCommonPinningStyles(header.column),
        }}
      >
        {tasksLoading ? (
          !SKELETON_BLACKLIST.includes(column.id) && (
            <>
              <span style={{ visibility: "hidden" }} />
              <SkeletonText width={60} height={15} />
            </>
          )
        ) : (
          <div
            className={classNames("tasks-table__header-wrapper", {
              "tasks-table__header-wrapper--active": isDropdownOpened,
            })}
          >
            {activeSortingColumn === header.id && (
              <SortingTasksArrow tableContext={tableContext} />
            )}
            {renderContent()}
            {!COLUMN_ID_BLACKLIST.includes(column.id) && columnIndex !== 2 && (
              <TaskTableHeaderOptionsDropdown
                uuid={header.id}
                setIsDropdownOpened={setIsDropdownOpened}
                tableContext={tableContext}
                activeSortingColumn={activeSortingColumn}
              />
            )}
          </div>
        )}
      </th>
    );
  }

  const headerCellContentCommonProps = {
    header,
    columnIndex,
    tasksLoading,
    draggable,
    isDropdownOpened,
    setIsDropdownOpened,
    activeSortingColumn,
    tableContext,
  };

  if (isTasksColumn(columnFromApi)) {
    return (
      <HeaderCellContentForTaskColumn
        {...headerCellContentCommonProps}
        draggable={draggable}
        columnFromApi={columnFromApi}
        tableContext={tableContext}
        draggedColumnId={draggedColumnId}
        isBeingDragged={isBeingDragged}
      />
    );
  }
  if (isColumn(columnFromApi)) {
    return (
      <HeaderCellContentForColumn
        {...headerCellContentCommonProps}
        draggable={draggable}
        columnFromApi={columnFromApi}
        tableContext={tableContext}
      />
    );
  }
  return (
    <th
      className={classNames("tasks-table__th", {
        "tasks-table__th--transparent-bg":
          tableContext === "workspaceTasksList",
      })}
      style={{
        ...getCommonPinningStyles(header.column),
      }}
    >
      &nbsp;
    </th>
  );
}

export { HeaderCell };
